// Code generated by tygo. DO NOT EDIT.
/* eslint-disable */
/* prettier-ignore */

//////////
// source: attachment.go

/**
 * AttachmentRef names one uploaded attachment as a sender offers it: the id the upload route
 * returned, and the two fields the sender may still change (protocol.md §2.6). An attachment is
 * core's (internal/core/files), so every module request that carries files names them with this one
 * shape — a chat message's send and edit, its draft, an AI chat's message — and a field added here
 * reaches every sender at once.
 * An empty Filename or Caption changes nothing: the server keeps the stored value, so a plain
 * attachment travels as its id alone.
 */
export interface AttachmentRef {
	id: number /* int64 */;
	filename?: string;
	caption?: string;
}

//////////
// source: codesearch.go

/**
 * CodeSearchRequest is the body of POST /api/search/code.
 * The query travels in a body and not in a query string for the reason the message search does: a
 * request line reaches the access log, the Referer header and the browser history, and what a
 * person searched for is theirs.
 */
export interface CodeSearchRequest {
	/**
	 * Q is what was typed, with the two operators still in it: `repo:<id>` narrows the search to a
	 * repository and `path:<prefix>` to a directory. Anything else of the shape `foo:bar` is part
	 * of the pattern, which is what somebody typing `TODO:` means.
	 */
	q: string;
	/**
	 * MatchCase overrides smart case, which is: case-insensitive unless the pattern holds a capital.
	 */
	matchCase?: boolean;
	/**
	 * WholeWords finds the pattern only where a word boundary is on both sides.
	 */
	wholeWords?: boolean;
	/**
	 * Limit is how many files one group answers with; zero takes the configured page.
	 */
	limit?: number /* int */;
	/**
	 * Cursor is a group's own `nextCursor`, and it is the whole of *Show more*: it carries the
	 * group it belongs to, the page and the commit every repository was searched at, so the next
	 * page continues the answer instead of re-cutting a corpus that has moved.
	 * There is no field for "answer this group only" beside it, because nothing a browser does
	 * needs one: the first search draws both sections, and every page after it is a cursor, which
	 * names its own group. The operator's verb reaches the same narrowing through the service.
	 */
	cursor?: string;
}
/**
 * CodeSearchResult is one answer.
 */
export interface CodeSearchResult {
	/**
	 * Parsed is the query as the search read it, so the screen draws its chips from what was
	 * actually searched for and not from a second parse of the same string.
	 */
	parsed: CodeSearchParsed;
	/**
	 * Groups are the asked-for groups, in the order they are drawn: documents, then code.
	 */
	groups: CodeSearchGroup[];
	/**
	 * Repos is every repository the search looked at, with the commit it was answered from. A
	 * screen that said "nothing found" about a repository with no clone would be stating something
	 * it does not know.
	 */
	repos: CodeSearchRepo[];
	/**
	 * Excluded says the exclusion list was in force over this answer: key and certificate material
	 * was neither opened nor listed, whatever was searched for. It is what lets the page say once,
	 * quietly, that keys and certificates are never searched — and it deliberately does not mean
	 * "this query would have reached a key", which would be a hint about what the clones hold.
	 */
	excluded: boolean;
	/**
	 * Refreshed says a page was asked to continue from a commit that is no longer in the clone —
	 * somebody rewrote a branch — and was answered at the current one. The rows are real; they are
	 * not cut from the same corpus as the page before them.
	 */
	refreshed: boolean;
	tookMs: number /* int64 */;
}
/**
 * CodeSearchParsed is what the query string turned out to say.
 */
export interface CodeSearchParsed {
	/**
	 * Text is the pattern that was searched for, with the operators removed.
	 */
	text: string;
	/**
	 * Repos and Path are the operators that were found, as the chips beside the field.
	 */
	repos?: string[];
	path?: string;
}
/**
 * CodeSearchGroup is one half of an answer: the pages a team writes, or the code.
 */
export interface CodeSearchGroup {
	/**
	 * Group is "documents" or "code".
	 */
	group: string;
	files: CodeSearchHit[];
	/**
	 * TotalFiles is how many files of this group hold the pattern, and TotalFilesCapped says the
	 * counting stopped — the heading then says "more than N files", which is true, where a number
	 * nobody counted would not be.
	 */
	totalFiles: number /* int */;
	totalFilesCapped: boolean;
	/**
	 * Truncated says a cap or the deadline stopped the scan: what is here is ranked and correct,
	 * and it is not all there is.
	 */
	truncated: boolean;
	/**
	 * NextCursor is what *Show more* sends back, and it is empty when there is no more.
	 */
	nextCursor?: string;
}
/**
 * CodeSearchHit is one file that matched, with the first of its matched lines.
 */
export interface CodeSearchHit {
	repo: string;
	path: string;
	/**
	 * Commit is what this file was read at, for a link that opens the same text the excerpt is cut
	 * from.
	 */
	commit: string;
	/**
	 * Title is what this page is called — its first heading, in plain text, by the one title rule
	 * (search/code's doctitle.go) — so a row reads as the page a person knows and not as a file
	 * name. It is set on the hits of the documents group only, and it is empty
	 * where the page could not be read in time; the screen then names the file.
	 */
	title?: string;
	/**
	 * Class is "normal", "unlisted", "generated" or "vendored": what kind of file this is, which is
	 * what the answer is ordered by first and what the quiet pill on the row says.
	 */
	class: string;
	/**
	 * PathMatch says the pattern is in the file's own name. A file whose name matches and whose
	 * text does not has no excerpts at all — which is how searching for a file name finds the file.
	 */
	pathMatch: boolean;
	/**
	 * MatchCount is how many lines of this file matched, and MatchCountCapped says the counting
	 * stopped there ("5+ matches").
	 */
	matchCount: number /* int */;
	matchCountCapped: boolean;
	/**
	 * Matches are the shown lines, in line order.
	 */
	matches: CodeSearchLine[];
}
/**
 * SnippetEllipsis is the text of the run a cut excerpt carries at an end where the window left
 * something out. A client that draws the excerpt draws it like any other run; a client that wants
 * the *line* — to find those words again in the file the hit came from — takes it off, because no
 * line of any file holds it.
 * It is written out here rather than aliased from core because this file is the generator's input
 * and an alias generates as `any`; codesearch_test.go holds the two spellings to one string.
 */
export const SnippetEllipsis = "…";
/**
 * SnippetPart is one run of an excerpt: the text, and whether it is a match the client draws as a
 * <mark>. It is the wire spelling of core.SnippetPart, which is the one the server reasons with,
 * and the mapping below is the one place the two meet — a browser-visible type of a core route is
 * generated from this package, and a field whose type lives in another package generates as `any`.
 */
export interface SnippetPart {
	t: string;
	m?: boolean;
}
/**
 * CodeSearchLine is one matched line as a row shows it.
 */
export interface CodeSearchLine {
	/**
	 * Line is the 1-based line number in the file as it is committed. Under an open draft it is a
	 * hint and the text is the anchor: search reads what was committed.
	 */
	line: number /* int */;
	/**
	 * Snippet is the line, cut to a window around its first match, in runs. The ones with m are the
	 * matches.
	 */
	snippet: SnippetPart[];
	/**
	 * Heading is the section of a page this line sits in: the nearest heading above it, in plain
	 * text (search/code's doctitle.go). It is empty in a file of the code group, under no heading, and
	 * under the heading the page is named after, which the row already shows as its title.
	 */
	heading?: string;
}
/**
 * CodeSearchRepo is one repository as an answer reports it.
 */
export interface CodeSearchRepo {
	id: string;
	commit?: string;
	/**
	 * FetchedAtMs is when this instance last fetched it, and zero when it never has. It is what the
	 * "these were last updated at …" line is drawn from.
	 */
	fetchedAtMs?: number /* int64 */;
	/**
	 * State is "ok", "missing" — no clone on this machine yet — or "failed".
	 */
	state: string;
}
/**
 * CodeFile is one tracked file as the code file page reads it: GET /api/search/code/file.
 */
export interface CodeFile {
	repo: string;
	path: string;
	/**
	 * Commit is what the file was read at: the hit's commit, or the tip when the link named none.
	 */
	commit: string;
	/**
	 * Size is the file's size in bytes, whether or not its text came with it.
	 */
	size: number /* int64 */;
	/**
	 * Text is the file, exactly as it is stored, and empty when Truncated.
	 */
	text: string;
	/**
	 * Truncated says the file is larger than the page draws (a megabyte): it is named with its size
	 * and no part of it is sent, because a prefix cut inside a line would show text nobody wrote.
	 */
	truncated: boolean;
}

//////////
// source: control.go

/**
 * StepUp is the pair of freshness stamps the client needs in order to decide whether to open the
 * step-up dialog before it sends a request that will be refused. Epoch-ms; 0 = never.
 */
export interface StepUp {
	passwordAt?: number /* int64 */;
	codeAt?: number /* int64 */;
}
/**
 * PublicUser is an account as another member sees it. Which fields are present depends on the
 * viewer's rung: the projection builds the value up field by field and never strips one down
 * (security.md §9.6).
 */
export interface PublicUser {
	id: number /* int64 */;
	handle: string;
	displayName: string;
	kind: string; // "human" | "bot"
	/**
	 * The standing, derived from the rows of @admins and @team when the account is read: "admin",
	 * "member" (a team member), "guest" (a person in neither), or "" for a bot.
	 */
	role: 'admin' | 'member' | 'guest' | '';
	/**
	 * "active" | "invited" | "deactivated". `invited` is an account whose invitation nobody has
	 * answered yet: the projection derives it from the last sign-in, which stays a self fact.
	 */
	state: string;
	avatarUrl?: string;
	title?: string;
	pronouns?: string;
	fullName?: string;
	/**
	 * The IANA zone a colleague works in ("Europe/Berlin"), from the member rung up. The client
	 * turns it into the time it is there; a time computed here would be stale on arrival.
	 */
	timezone?: string;
	/**
	 * The status the person publishes about themselves, from the member rung up: one emoji of
	 * StatusEmojiPalette, a short line, the moment it clears itself (epoch-ms UTC, absent = never)
	 * and the "show me as away" claim. It is the opposite of presence, which is observed by the
	 * transport and stored nowhere (security.md §9.6). StatusUntil is sent rather than resolved
	 * here, because the client draws "until 13:00" in the reader's own clock; a status whose moment
	 * has passed but whose row the minutely expiry job has not reached yet is still drawn, since
	 * the server's clock decides when it goes and not the reader's.
	 */
	statusEmoji?: string;
	statusText?: string;
	statusUntil?: number /* int64 */;
	statusAway?: boolean;
}
/**
 * SelfUser is the account as its owner sees it: the public projection plus the fields nobody else
 * receives. PublicUser's fields are written out rather than embedded, because the JSON shape is
 * flat and the TypeScript generator renders an embedded struct as a nested property.
 */
export interface SelfUser {
	id: number /* int64 */;
	handle: string;
	displayName: string;
	kind: string; // "human" | "bot"
	/**
	 * The standing, derived from the rows of @admins and @team when the account is read: "admin",
	 * "member" (a team member), "guest" (a person in neither), or "" for a bot.
	 */
	role: 'admin' | 'member' | 'guest' | '';
	/**
	 * "active" | "invited" | "deactivated", filled from the same projection as PublicUser.State —
	 * and the third value cannot reach this rung, because a session exists only after a sign-in and
	 * a sign-in is what stamps the column `invited` is derived from.
	 */
	state: string;
	avatarUrl?: string;
	title?: string;
	pronouns?: string;
	fullName?: string;
	/**
	 * The owner's own status, the same four fields every other reader receives (PublicUser): the
	 * screen that sets it reads back what was stored, and the account menu draws it from `me`.
	 */
	statusEmoji?: string;
	statusText?: string;
	statusUntil?: number /* int64 */;
	statusAway?: boolean;
	email: string;
	locale: string;
	timezone: string;
	stepUp: StepUp;
	hasPassword: boolean;
}
/**
 * NotificationPrefView is one account's notification settings as the client renders them.
 */
export interface NotificationPrefView {
	emailPolicy: string;
	emailDelayMinutes: number /* int */;
	mailIncludeContent: boolean;
	desktopEnabled: boolean;
	soundEnabled: boolean;
}
/**
 * Hello is the first frame of every connection; nothing is dispatched before it. Csrf is compared
 * against the session's derived synchroniser token, which is what stops a cross-origin socket.
 */
export interface Hello {
	protocol: number /* int */;
	client: string; // "web/<sha>"
	csrf: string;
	locale?: string; // "de" | "en"
}
/**
 * HelloMe is the caller's own identity as the handshake reply carries it: enough for the client to
 * render its shell before the bootstrap payload arrives.
 */
export interface HelloMe {
	userId: number /* int64 */;
	/**
	 * The standing, derived from @admins and @team: "admin", "member", "guest", or "" for a bot.
	 */
	role: 'admin' | 'member' | 'guest' | '';
	sessionId: number /* int64 */;
	stepUp: StepUp;
}
/**
 * HelloResult answers rt.hello. ServerBootId changes on every restart and is informational: a
 * restart is invisible to the client, because sessions, cursors and the change log are rows.
 * ChangeLogFloor is the oldest event still replayable — a client below it must resync.
 * HeartbeatMs is the cadence of the server's WebSocket pings; ReadDeadlineMs is the silence after
 * which the server hangs up, and the number the client derives its own keepalive interval from, so
 * that one side owns the timing. ReadDeadlineMs is optional: a client that does not see it falls
 * back to its own default.
 */
export interface HelloResult {
	me: HelloMe;
	serverBootId: string;
	serverTime: number /* int64 */;
	heartbeatMs: number /* int */;
	readDeadlineMs?: number /* int */;
	limits: Limits;
	eventId: number /* int64 */;
	changeLogFloor: number /* int64 */;
}
/**
 * SyncRequest asks for everything that happened since SinceEventId. ConvSeq lets the server detect a
 * per-conversation gap without replaying the whole log; it is omitted above 200 entries, and the
 * server then answers with a resync instead.
 */
export interface SyncRequest {
	sinceEventId: number /* int64 */;
	convSeq?: { [key: string]: number /* int64 */}; // conversation id as a decimal string → last seq
}
/**
 * SyncResult is the catch-up: the events in order, the id the client is now at, and — when the gap
 * was too wide to close — the resync that replaces them.
 */
export interface SyncResult {
	events: Envelope[];
	eventId: number /* int64 */;
	resync?: Resync;
}
/**
 * Resync tells one connection to throw away what it has and load it again, either everything or the
 * named conversations. Reason is for the log and the client's telemetry, never for a message to the
 * user.
 */
export interface Resync {
	scope: string; // "all" | "conv"
	reason: string; // overflow | stale | gap | toolong | toobig | protocol | restored
	convs?: number /* int64 */[];
}
/**
 * Draining is the last frame before a graceful shutdown closes the connection. ReconnectAfterMs is
 * drawn per connection so a restart does not bring every client back at the same millisecond.
 */
export interface Draining {
	reconnectAfterMs: number /* int64 */;
}
/**
 * Focus is the scope the client has on screen in a visible tab: {kind, id} — a conversation, an AI
 * chat, any kind a module resolves — or the older {conv}, which names a conversation. Both at once
 * are refused; {} is nothing on screen. It feeds the notification decision, a scope's viewers and the
 * frames only a watcher needs (protocol.md §2.5.1). A hidden tab reports the scope its page shows with
 * Hidden: that is no focus, but the tab still has the scope open, which the scope's owner may weigh (an
 * AI chat open in a background tab keeps its container a while longer); Hidden with nothing named is
 * nothing on screen.
 * Away is how long, in milliseconds, the tab has been away when the report is sent: its window lost
 * focus or it was hidden presenceGraceMs or more ago, or nobody touched the page for presenceAwayMs.
 * The browser times it because only the browser knows those facts; the server turns the duration
 * into a time of its own clock, so no client clock is trusted. It is presence only: a window that
 * lost focus keeps its focus for every other purpose.
 */
export interface Focus {
	conv?: number /* int64 */;
	kind?: string;
	id?: number /* int64 */;
	hidden?: boolean;
	away?: number /* int64 */;
}
/**
 * PingResult answers rt.ping. The application-level ping exists because a browser cannot observe
 * protocol pings: this reply is what lets the client detect a half-open socket on its side.
 */
export interface PingResult {
	serverTime: number /* int64 */;
}
/**
 * BootstrapPayload is the cold-start snapshot. Core builds every field here; each enabled module
 * contributes one opaque object under Modules through its registered contributor, so core never
 * imports a module's wire package and the payload composes without a central list. It carries no
 * message and no draft body — the open conversation's tail and a draft's text are separate requests.
 */
export interface BootstrapPayload {
	me: SelfUser;
	users: PublicUser[];
	groups: GroupView[];
	activityUnread: number /* int */;
	settings: { [key: string]: unknown};
	notificationPrefs: NotificationPrefView;
	limits: Limits;
	serverBootId: string;
	serverTime: number /* int64 */;
	eventId: number /* int64 */;
	changeLogFloor: number /* int64 */;
	modules: { [key: string]: unknown};
	/**
	 * EnabledModules names the modules this instance runs, sorted, so that the client draws no
	 * place for a module that is switched off — a door into addresses that all answer 404. It is
	 * not the key set of Modules: a module with nothing to contribute at cold start is enabled
	 * and has no entry there.
	 * Absent means "this server does not say", which the client reads as "draw what you have": the
	 * behaviour of every build from before the field existed.
	 */
	enabledModules?: string[];
}

//////////
// source: envelope.go
/*
Package wire is the JSON vocabulary of both transports: the socket envelope, the one error shape,
the served limits and the control frames. It is a tygo input, so every type here is a plain struct
with a json tag on every field, and json.RawMessage stands in wherever the shape belongs to
another owner (protocol.md §5.1). Error.Args is the one documented exception: protocol.md §1.1
types it map[string]any because a locale placeholder takes any scalar.
*/

/**
 * Kind is the frame class. A client may send nothing but "req"; the four others are the server's.
 */
export type Kind = string;
export const KindReq: Kind = "req";
export const KindRes: Kind = "res";
export const KindAck: Kind = "ack";
export const KindEvt: Kind = "evt";
export const KindErr: Kind = "err";
/**
 * Envelope is every frame on the socket. Id, Conv, Seq and EventId are present only where the type
 * earns them: Id on request-shaped frames, Conv on conversation traffic, Seq only on the events that
 * allocate one, EventId on every durable event and on an ack whose request committed a change.
 */
export interface Envelope {
	v: number /* int */; // always 1
	kind: Kind;
	id?: number /* int64 */; // req/res/ack/err: client-minted, > 0, strictly increasing per connection
	type: string; // on res/ack/err = the request's type
	conv?: number /* int64 */; // evt with a conversation audience; ack of a conversation mutation
	seq?: number /* int64 */; // only on seq-allocating events and their acks
	eventId?: number /* int64 */; // every durable evt; ack when the request committed a change row
	data: unknown; // an object; {} when empty; never null
}

//////////
// source: error.go

/**
 * ErrorCode is the closed set of failure classes both transports share. HTTP maps it to a status,
 * the socket carries it as the data of a kind:"err" frame.
 */
export type ErrorCode = string;
export const ErrInvalid: ErrorCode = "invalid"; // 400 — malformed, over a cap, failed validation
export const ErrUnauthenticated: ErrorCode = "unauthenticated"; // 401 — no or expired session
export const ErrForbidden: ErrorCode = "forbidden"; // 403 — policy denial with a visible ref
export const ErrStepUpRequired: ErrorCode = "stepUpRequired"; // 403 — the client opens the step-up dialog
export const ErrCSRF: ErrorCode = "csrf"; // 403 — origin or synchroniser token
export const ErrNotFound: ErrorCode = "notFound"; // 404 — also every ref the actor may not see
export const ErrConflict: ErrorCode = "conflict"; // 409 — Detail carries the current state
export const ErrGone: ErrorCode = "gone"; // 410 — tombstoned, burned, expired
export const ErrTooLarge: ErrorCode = "tooLarge"; // 413
export const ErrRateLimited: ErrorCode = "rateLimited"; // 429 — RetryAfterMs set
export const ErrUnavailable: ErrorCode = "unavailable"; // 503 — draining, disk, DB busy; RetryAfterMs set
export const ErrInsufficient: ErrorCode = "insufficientStorage"; // 507 — quota or free-space floor
export const ErrInternal: ErrorCode = "internal"; // 500
export const ErrProtocol: ErrorCode = "protocol"; // socket only — version or envelope violation
/**
 * Error is the only error shape on the wire. Key is a locale key the client renders through its
 * catalogue with Args interpolated; the server never sends prose. Detail is the typed extra a few
 * keys carry, such as the current body on an edit conflict.
 */
export interface Error {
	code: ErrorCode;
	key: string;
	args?: { [key: string]: any}; // scalars only
	retryAfterMs?: number /* int64 */;
	detail?: unknown;
	requestId?: string; // HTTP only; joins the slog line
}
/**
 * ErrorBody is how HTTP carries the error: one object with one key, so a client parses success and
 * failure the same way.
 */
export interface ErrorBody {
	error: Error;
}

//////////
// source: errorfor.go

/**
 * KeyInternal is the one key an unexpected failure ever produces. It has no arguments and no
 * detail: everything a caller may learn about a 500 is its request id.
 */
export const KeyInternal = "error.internal";
/**
 * The transport's own failures: the ones that belong to no service, because the request never
 * reached one. Both halves of each are here — the bare code a caller hands core.Invalid and the key
 * the wire spells it as (LocaleKey below) — so the client catalogue can be written against this
 * file instead of against a literal in whichever adapter happened to produce the refusal (I20).
 */
export const CodeBadRequest = "rt.badRequest"; // the body is not the request it claims to be
/**
 * The transport's own failures: the ones that belong to no service, because the request never
 * reached one. Both halves of each are here — the bare code a caller hands core.Invalid and the key
 * the wire spells it as (LocaleKey below) — so the client catalogue can be written against this
 * file instead of against a literal in whichever adapter happened to produce the refusal (I20).
 */
export const CodeTooLarge = "rt.tooLarge"; // over the route's body limit: a 413
/**
 * The transport's own failures: the ones that belong to no service, because the request never
 * reached one. Both halves of each are here — the bare code a caller hands core.Invalid and the key
 * the wire spells it as (LocaleKey below) — so the client catalogue can be written against this
 * file instead of against a literal in whichever adapter happened to produce the refusal (I20).
 */
export const CodeFrontendMissing = "rt.frontendMissing"; // this binary carries no built frontend
/**
 * The transport's own failures: the ones that belong to no service, because the request never
 * reached one. Both halves of each are here — the bare code a caller hands core.Invalid and the key
 * the wire spells it as (LocaleKey below) — so the client catalogue can be written against this
 * file instead of against a literal in whichever adapter happened to produce the refusal (I20).
 */
export const KeyBadRequest = "error." + CodeBadRequest;
/**
 * The transport's own failures: the ones that belong to no service, because the request never
 * reached one. Both halves of each are here — the bare code a caller hands core.Invalid and the key
 * the wire spells it as (LocaleKey below) — so the client catalogue can be written against this
 * file instead of against a literal in whichever adapter happened to produce the refusal (I20).
 */
export const KeyTooLarge = "error." + CodeTooLarge;
/**
 * The transport's own failures: the ones that belong to no service, because the request never
 * reached one. Both halves of each are here — the bare code a caller hands core.Invalid and the key
 * the wire spells it as (LocaleKey below) — so the client catalogue can be written against this
 * file instead of against a literal in whichever adapter happened to produce the refusal (I20).
 */
export const KeyFrontendMissing = "error." + CodeFrontendMissing;

//////////
// source: events.go

/**
 * SessionRevoked is the last frame a revoked session's connections receive before close 4401.
 * Reason is one of logout · revoked · passwordChange · roleChange · deviceRevoked · deactivated ·
 * admin · expired; the client renders it through its catalogue on the login page. A visitor by link
 * is closed with link_closed when its link no longer reaches what it shows; its own page says so.
 */
export interface SessionRevoked {
	reason: string;
}
/**
 * AccountUpdated carries the complete public view of an account after any change — one payload for
 * everybody, at the member rung, never a patch of the fields that moved.
 * Complete is the contract both sides are built on. A client replaces the account it holds with
 * this value, so an optional field the payload does not name is empty and not unchanged: no
 * picture, no title. The owner's own connections receive the same public view and merge it into
 * the self view they hold, keeping the fields no other rung receives — the address, the locale,
 * the step-up stamps.
 */
export interface AccountUpdated {
	user: PublicUser;
}
/**
 * PolicyChanged tells a connection that what it may do has changed; the client re-runs
 * rt.bootstrap. Enforcement never depends on it — permission is re-checked at delivery and at
 * every request. Cause is one of role · killswitch · membership · revoked.
 */
export interface PolicyChanged {
	cause: string;
}
/**
 * SettingsChanged names an instance setting whose value moved. The value is refetched, never
 * pushed, so a setting that must not reach every admin at once has no path to do so.
 */
export interface SettingsChanged {
	key: string;
	version: number /* int */;
}
/**
 * UiSettingsChanged carries the owner's changed user settings, so every tab converges without
 * a refetch. Values are JSON scalars of the setting's own type.
 */
export interface UiSettingsChanged {
	settings: { [key: string]: unknown};
}
/**
 * ActivityItemView is one row of the activity feed as the client renders it: a reply, a mention,
 * a reaction or a membership change, pointing at the message and conversation it happened in.
 * Data is the kind's own extra, rendered through the ActivityRenderer registered for the kind.
 */
export interface ActivityItemView {
	id: number /* int64 */;
	kind: string;
	conv?: number /* int64 */;
	messageId?: number /* int64 */;
	seq?: number /* int64 */;
	actorUserId?: number /* int64 */;
	createdAt: number /* int64 */;
	readAt?: number /* int64 */;
	data?: unknown;
}
/**
 * ActivityItem is the activity.item event: one row of the recipient's feed, either new or raised.
 * New says which of the two this delivery is — false when the row was already in the feed and only
 * its count, its seq and its time moved (core.ActivityItem.Coalesce) — so a client raises its badge
 * once per row and not once per message. The flag belongs to this delivery rather than to
 * ActivityItemView, which is also what the feed route answers with and where "new" would mean
 * nothing.
 */
export interface ActivityItem {
	item: ActivityItemView;
	new: boolean;
}
/**
 * ActivityRead is the activity.read event. Either Ids names the rows that were read, or Conv and
 * ThroughSeq say that moving a read cursor cleared every row of a conversation up to a seq.
 */
export interface ActivityRead {
	ids?: number /* int64 */[];
	conv?: number /* int64 */;
	throughSeq?: number /* int64 */;
	readAt: number /* int64 */;
}
/**
 * NotifyHint is the notification decision for one message, not the notification itself: the
 * client picks a toast or a browser Notification. Preview is nil when the content must not leave
 * the server (mail content off, or a direct message). Reason is one of directMention ·
 * channelMention · dm · all.
 */
export interface NotifyHint {
	conv: number /* int64 */;
	messageId: number /* int64 */;
	seq: number /* int64 */;
	reason: string;
	preview?: string;
	actorUserId: number /* int64 */;
	conversationName: string;
}
/**
 * FileScanChanged reports the virus-scan state of attachments, only while ClamAV is on. ScanState
 * is one of pending · clean · infected.
 */
export interface FileScanChanged {
	conv: number /* int64 */;
	attachmentIds: number /* int64 */[];
	scanState: string;
}
/**
 * AttachmentWire is an attachment as a message carries it and as the upload routes answer
 * (protocol.md §2.6). State is ready or removed; ScanState is present only while scanning is on.
 * PixelRatio is present only for a picture that states a density of about 108 dpi or more: how many
 * of its pixels make one CSS pixel (2 for a screenshot taken on a Retina screen, at most 4), so a
 * document draws it at Width divided by it; absent means 1.
 */
export interface AttachmentWire {
	id: number /* int64 */;
	filename: string;
	caption: string;
	bytes: number /* int64 */;
	mime: string;
	kind: string;
	sha8: string;
	width?: number /* int */;
	height?: number /* int */;
	pixelRatio?: number /* int */;
	url: string;
	thumbUrl: string;
	previewUrl: string;
	inlineOk: boolean;
	position: number /* int */;
	state: string;
	scanState?: string;
}

//////////
// source: group.go

/**
 * GroupView is a named group of accounts. DeletedAt is set only on the last event a group emits —
 * a tombstone the client uses to drop the group and then never sees again.
 * Builtin marks the groups the instance makes itself and is omitted for a group people made. The
 * three special groups ("admins", "team", "all") list their member rows like any group, deactivated
 * people included; a person's standing is their rows in @admins and @team. A special's displayName
 * is its handle until somebody renames it — a client draws its own copy name in the meantime. A
 * personal group ("personal") holds exactly the account PersonalUserId names and is drawn as that
 * person; it never reaches the bootstrap payload or an event, only the list route's
 * ?include=personal and a read by id.
 */
export interface GroupView {
	id: number /* int64 */;
	handle: string;
	displayName: string;
	mentionable: boolean;
	memberIds: number /* int64 */[];
	createdAt: number /* int64 */;
	updatedAt: number /* int64 */;
	deletedAt?: number /* int64 */;
	builtin?: 'admins' | 'team' | 'all' | 'personal';
	personalUserId?: number /* int64 */;
}
/**
 * GroupChanged carries the whole group after any mutation — create, rename, flag change, member add
 * or remove, delete. Whole items, never diffs: the client replaces its copy and needs no ordering
 * beyond the event id.
 */
export interface GroupChanged {
	group: GroupView;
}
/**
 * TypeAccountGroupChanged is the one event the group directory emits.
 */
export const TypeAccountGroupChanged = "account.group.changed";

//////////
// source: limits.go

/**
 * Limits is every numeric cap the client must mirror. It is served in the rt.hello reply and in the
 * bootstrap payload and is never hard-coded on the client: a cap that changes on the server changes
 * on the next connection, not on the next release (protocol.md §2.5.1).
 * Two of them time the faces in a document's band (DV W-4). PresenceAwayMs is how long a page may go
 * untouched before its person counts as away: the browser's input clock reads it for the faces, and
 * the hub's silence on every connection reads it for chat.presence, so the two never disagree.
 * PresenceGraceMs is how long a window may have lost focus, or a tab been hidden, before the browser
 * reports it away, so a glance at another window never fades a face.
 */
export interface Limits {
	maxFrameBytes: number /* int64 */;
	sendsPer10s: number /* int */;
	maxCatchupEvents: number /* int */;
	maxMarkdownChars: number /* int */;
	maxMentions: number /* int */;
	maxAttachmentsPerMessage: number /* int */;
	maxFileBytes: number /* int64 */;
	maxMessageBytes: number /* int64 */;
	inlinePreviewMaxBytes: number /* int64 */;
	draftMaxBytes: number /* int64 */;
	typingIntervalMs: number /* int */;
	presenceCoalesceMs: number /* int */;
	presenceAwayMs: number /* int */;
	presenceGraceMs: number /* int */;
	historyPageMax: number /* int */;
	searchLimitMax: number /* int */;
}

//////////
// source: markdown.go

/**
 * NodeType is the discriminator of Node. The set is closed: a parser that would emit anything else
 * emits a text node instead, and a client that meets an unknown type renders a visible placeholder
 * (REUSE G1).
 */
export type NodeType = string;
export const NodeParagraph: NodeType = "paragraph";
export const NodeHeading: NodeType = "heading";
export const NodeQuote: NodeType = "quote";
export const NodeList: NodeType = "list";
export const NodeItem: NodeType = "item";
/**
 * NodeCode is a fenced block only; indented code blocks are off (spec 06 §1).
 */
export const NodeCode: NodeType = "code";
export const NodeTable: NodeType = "table";
export const NodeRow: NodeType = "row";
export const NodeCell: NodeType = "cell";
export const NodeRule: NodeType = "rule";
export const NodeText: NodeType = "text";
export const NodeEm: NodeType = "em";
export const NodeStrong: NodeType = "strong";
export const NodeStrike: NodeType = "strike";
export const NodeCodeSpan: NodeType = "codespan";
export const NodeLink: NodeType = "link";
/**
 * NodeBreak covers soft and hard breaks alike: a single newline is a line break (Q20).
 */
export const NodeBreak: NodeType = "break";
/**
 * NodePermalink is an internal /c/<ref>/p/<seq> chip, never a network fetch (Q23 a).
 */
export const NodePermalink: NodeType = "permalink";
/**
 * NodeImage renders as a link: no remote <img> is ever loaded (Q23 a).
 */
export const NodeImage: NodeType = "image";
/**
 * NodeMath is reserved. The parser never emits it in v1 and no renderer interprets LaTeX.
 */
export const NodeMath: NodeType = "math";
export const NodeMention: NodeType = "mention";
export const NodeEmoji: NodeType = "emoji";
/**
 * Render flags. The bitset travels with the tree so a client can style a message without walking it
 * (schema.md §3.1: chat_message_render.flags).
 */
export const FlagJumbo = 1 << 0; // up to three emoji clusters and nothing else: render them large (A29)
/**
 * Render flags. The bitset travels with the tree so a client can style a message without walking it
 * (schema.md §3.1: chat_message_render.flags).
 */
export const FlagHasCode = 1 << 1;
/**
 * Render flags. The bitset travels with the tree so a client can style a message without walking it
 * (schema.md §3.1: chat_message_render.flags).
 */
export const FlagHasTable = 1 << 2;
/**
 * Render flags. The bitset travels with the tree so a client can style a message without walking it
 * (schema.md §3.1: chat_message_render.flags).
 */
export const FlagHasMath = 1 << 3; // reserved with the math node
/**
 * Render flags. The bitset travels with the tree so a client can style a message without walking it
 * (schema.md §3.1: chat_message_render.flags).
 */
export const FlagTruncated = 1 << 4; // the tree hit the byte budget and carries a trailing marker node
/**
 * Render flags. The bitset travels with the tree so a client can style a message without walking it
 * (schema.md §3.1: chat_message_render.flags).
 */
export const FlagHasMention = 1 << 5;
/**
 * Render flags. The bitset travels with the tree so a client can style a message without walking it
 * (schema.md §3.1: chat_message_render.flags).
 */
export const FlagHasLink = 1 << 6;
/**
 * Node is one node of a rendered message tree, a discriminated union on Type. Every string field
 * carries literal characters, never markup: the renderer is a closed registry and nothing here is
 * ever interpreted as HTML.
 */
export interface Node {
	type: NodeType;
	children?: Node[];
	text?: string; // text, codespan, code: the literal characters
	src?: number /* int */[]; // block nodes only: [startByte, endByte] into the message
	level?: number /* int */; // heading, 1..6 (rendered clamped, spec 06 §4)
	ordered?: boolean; // list
	start?: number /* int */; // list, honoured when != 1
	tight?: boolean; // list
	checked?: boolean; // item: nil = not a task item
	lang?: string; // code: info string, first word, ^[a-z0-9+#.-]{0,24}$
	align?: string[]; // table: "", "left", "center", "right" per column
	head?: boolean; // row: header row
	href?: string; // link, image: scheme already validated (spec 06 §5)
	title?: string; // link, image
	alt?: string; // image
	auto?: boolean; // link: came from an autolink literal
	hrefHost?: string; // link: set only when the link *text* is a URL of
	perma?: Permalink;
	mention?: Mention;
	emoji?: EmojiRef;
	latex?: string; // math (reserved, never emitted in v1)
	inline?: boolean; // math (reserved)
}
/**
 * Permalink is an internal link to one message. The parser sees only the URL, so it fills Kind and
 * Ref — the route segments the client already knows how to navigate — and leaves ConversationId 0;
 * a caller that has the database resolves Ref and fills it in, the same shape of two-step resolution
 * as Mention (spec 05 O-6: /c/<slug>/p/<seq>, /d/<handle>/p/<seq>, /c/g<id>/p/<seq>).
 */
export interface Permalink {
	conversationId: number /* int64 */;
	kind: string; // "c" for a channel-style ref, "d" for a DM handle
	ref: string; // the slug or DM handle as it stood in the link
	seq: number /* int64 */;
}
/**
 * Mention keeps the typed source ("@anna", "@designers", "#allgemein") and, once resolved, the
 * target id. The body always stores the token verbatim (Q20). Kind names what the token is:
 *   - "" for an `@handle` the parser left unresolved, which a module's resolver turns into "user" or
 *     "group" before the tree is cached — a handle resolves to a user first, else to a mentionable
 *     group, else the token stays a text node;
 *   - a kind a module registered (core.MentionTokenDef: chat's "channel" and "conversation"), which
 *     the parser settles itself and which names no row, so Id stays 0.
 */
export interface Mention {
	kind: string; // "user" | "group" | a registered kind; "" = unresolved
	handle: string; // without the '@' or the '#'
	id?: number /* int64 */; // account id for "user", account_group id for "group"
}
/**
 * EmojiRef is emitted for a literal Unicode emoji *and* for a resolved :shortcode:, so both render
 * identically and the jumbo rule has one path.
 */
export interface EmojiRef {
	char: string; // NFC unicode sequence, "" for a custom emoji
	shortcode?: string; // canonical shortcode, no colons
	name?: string; // CLDR name, only for the title attribute
	custom?: string; // custom emoji name (reserved)
}
/**
 * Render is what ships with a message: the cached tree and the version that produced it. A negative
 * Ver is a degraded render — the parse failed at |Ver| and the body is shown as one paragraph.
 * Nodes is never nil on the wire; every producer passes it through the Nodes function below.
 */
export interface Render {
	ver: number /* int */;
	flags: number /* int */;
	nodes: Node[];
}

//////////
// source: status.go

/**
 * The closed emoji palette of the status a person publishes about themselves (protocol.md §2.6).
 * It is published rather than agreed: the server refuses an emoji that is not on this line, and the
 * client's picker offers this line and nothing else, so there is exactly one place where a palette
 * is decided and no way for the two halves to drift apart.
 * It is one space-separated string and not a `[]string` because tygo renders a Go `const` and skips
 * a `var`: a slice here would generate nothing at all, and the client would end up typing the
 * twenty-four characters a second time — which is the one thing a closed set must not do. No emoji
 * contains a space, so `StatusEmojis()` on this side and `.split(" ")` on the other read the same
 * members in the same order, and that order is the order a picker draws.
 * Order is meaning, not taste: the first half is what a working day actually holds — a meeting, a
 * meal, illness, leave, the way in — and the second half is the quieter kind of absence. Adding an
 * emoji is a change to this line and to nothing else. Removing one does not reach the accounts that
 * already hold it: their status keeps its emoji and keeps being drawn until its owner changes it,
 * because the palette bounds what may be *written*, and rewriting what somebody published about
 * themselves is not ours to do.
 */
export const StatusEmojiPalette = "🗓️ 🍽️ 🤒 🌴 🚌 💤 🎧 🏠 ✈️ 🚗 👶 🎓 ☕ 🏃 📞 💻 🔕 🎉 🤝 ✍️ 🧘 🚧 🌙 ⏳";

//////////
// source: types.go

/**
 * Client → server requests owned by core (protocol.md §2.5.1).
 */
export const TypeRtHello = "rt.hello";
/**
 * Client → server requests owned by core (protocol.md §2.5.1).
 */
export const TypeRtBootstrap = "rt.bootstrap";
/**
 * Client → server requests owned by core (protocol.md §2.5.1).
 */
export const TypeRtSync = "rt.sync";
/**
 * Client → server requests owned by core (protocol.md §2.5.1).
 */
export const TypeRtPing = "rt.ping";
/**
 * Client → server requests owned by core (protocol.md §2.5.1).
 */
export const TypeRtFocus = "rt.focus";
/**
 * Server → client events owned by core (protocol.md §2.5.1, §2.5.2, §2.5.8).
 * TypeAccountGroupChanged is declared in group.go, one file per subject.
 */
export const TypeRtResync = "rt.resync";
/**
 * Server → client events owned by core (protocol.md §2.5.1, §2.5.2, §2.5.8).
 * TypeAccountGroupChanged is declared in group.go, one file per subject.
 */
export const TypeRtDraining = "rt.draining";
/**
 * Server → client events owned by core (protocol.md §2.5.1, §2.5.2, §2.5.8).
 * TypeAccountGroupChanged is declared in group.go, one file per subject.
 */
export const TypeSessionRevoked = "session.revoked";
/**
 * Server → client events owned by core (protocol.md §2.5.1, §2.5.2, §2.5.8).
 * TypeAccountGroupChanged is declared in group.go, one file per subject.
 */
export const TypeAccountUpdated = "account.updated";
/**
 * Server → client events owned by core (protocol.md §2.5.1, §2.5.2, §2.5.8).
 * TypeAccountGroupChanged is declared in group.go, one file per subject.
 */
export const TypePolicyChanged = "policy.changed";
/**
 * Server → client events owned by core (protocol.md §2.5.1, §2.5.2, §2.5.8).
 * TypeAccountGroupChanged is declared in group.go, one file per subject.
 */
export const TypeSettingsChanged = "settings.changed";
/**
 * Server → client events owned by core (protocol.md §2.5.1, §2.5.2, §2.5.8).
 * TypeAccountGroupChanged is declared in group.go, one file per subject.
 */
export const TypeUiSettingsChanged = "ui.settings.changed";
/**
 * Server → client events owned by core (protocol.md §2.5.1, §2.5.2, §2.5.8).
 * TypeAccountGroupChanged is declared in group.go, one file per subject.
 */
export const TypeActivityItem = "activity.item";
/**
 * Server → client events owned by core (protocol.md §2.5.1, §2.5.2, §2.5.8).
 * TypeAccountGroupChanged is declared in group.go, one file per subject.
 */
export const TypeActivityRead = "activity.read";
/**
 * Server → client events owned by core (protocol.md §2.5.1, §2.5.2, §2.5.8).
 * TypeAccountGroupChanged is declared in group.go, one file per subject.
 */
export const TypeNotifyHint = "notify.hint";
/**
 * Server → client events owned by core (protocol.md §2.5.1, §2.5.2, §2.5.8).
 * TypeAccountGroupChanged is declared in group.go, one file per subject.
 */
export const TypeNotifyPrefsChanged = "notify.prefs.changed";
/**
 * Server → client events owned by core (protocol.md §2.5.1, §2.5.2, §2.5.8).
 * TypeAccountGroupChanged is declared in group.go, one file per subject.
 */
export const TypeFileScanChanged = "file.scanChanged";
/**
 * Close codes. 1000–1011 are the WebSocket standard's; the 44xx block is teamBase's own, and a
 * client that sees 4401, 4403 or 4409 does not reconnect on its own (protocol.md §2.1).
 */
export const CloseNormal = 1000; // clean shutdown of one connection
/**
 * Close codes. 1000–1011 are the WebSocket standard's; the 44xx block is teamBase's own, and a
 * client that sees 4401, 4403 or 4409 does not reconnect on its own (protocol.md §2.1).
 */
export const CloseGoingAway = 1001; // drain, after rt.draining
/**
 * Close codes. 1000–1011 are the WebSocket standard's; the 44xx block is teamBase's own, and a
 * client that sees 4401, 4403 or 4409 does not reconnect on its own (protocol.md §2.1).
 */
export const CloseProtocolViolate = 1008; // a frame before hello, or malformed frames past the tolerance
/**
 * Close codes. 1000–1011 are the WebSocket standard's; the 44xx block is teamBase's own, and a
 * client that sees 4401, 4403 or 4409 does not reconnect on its own (protocol.md §2.1).
 */
export const CloseInternal = 1011; // internal failure or read deadline
/**
 * Close codes. 1000–1011 are the WebSocket standard's; the 44xx block is teamBase's own, and a
 * client that sees 4401, 4403 or 4409 does not reconnect on its own (protocol.md §2.1).
 */
export const CloseSessionInvalid = 4401; // session revoked or expired
/**
 * Close codes. 1000–1011 are the WebSocket standard's; the 44xx block is teamBase's own, and a
 * client that sees 4401, 4403 or 4409 does not reconnect on its own (protocol.md §2.1).
 */
export const CloseOriginOrCSRF = 4403; // origin or synchroniser token mismatch
/**
 * Close codes. 1000–1011 are the WebSocket standard's; the 44xx block is teamBase's own, and a
 * client that sees 4401, 4403 or 4409 does not reconnect on its own (protocol.md §2.1).
 */
export const CloseHandshake = 4408; // no hello within 5 s, or no sync/bootstrap within 10 s
/**
 * Close codes. 1000–1011 are the WebSocket standard's; the 44xx block is teamBase's own, and a
 * client that sees 4401, 4403 or 4409 does not reconnect on its own (protocol.md §2.1).
 */
export const CloseProtocolVersion = 4409; // hello.protocol is not 1
/**
 * Close codes. 1000–1011 are the WebSocket standard's; the 44xx block is teamBase's own, and a
 * client that sees 4401, 4403 or 4409 does not reconnect on its own (protocol.md §2.1).
 */
export const CloseFrameTooLarge = 4413; // a frame above MaxFrameBytes
/**
 * Close codes. 1000–1011 are the WebSocket standard's; the 44xx block is teamBase's own, and a
 * client that sees 4401, 4403 or 4409 does not reconnect on its own (protocol.md §2.1).
 */
export const CloseRateLimited = 4429; // a bucket emptied three times
/**
 * ProtocolVersion is the v of every envelope, the N of the teambase.v<N>.json subprotocol and the
 * protocol field of rt.hello: one number, asserted equal in all three places.
 */
export const ProtocolVersion = 1;
/**
 * Subprotocol is the required Sec-WebSocket-Protocol value; a missing or foreign subprotocol is a
 * 426 before the upgrade.
 */
export const Subprotocol = "teambase.v1.json";
